import { Mark, mergeAttributes, Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment } from '@tiptap/pm/model';
import { v4 } from 'uuid';
import { convertToFbc } from './LegacyConverters.js';
import { ySyncPluginKey } from 'y-prosemirror';
import { isDefined } from '@fabric-app/utils/guards';
import { ANNOTATION_DEFAULT_COLOR } from '../../../annotation/config.js';

const updateReplaceOldHighlight = (node, state) => {
  const schema = state.schema;
  const highlightType = schema.marks.highlight;
  const annotationType = schema.marks.annotation;
  if (!highlightType || !annotationType) return node;
  const marks = node.marks;
  if (marks.length === 0 && node.childCount === 0) {
    return node;
  }
  const newMarks = marks.length > 0 ? marks.map((mark) => {
    if (mark.type === highlightType) {
      return annotationType.create({
        annotationId: v4(),
        color: ANNOTATION_DEFAULT_COLOR,
        commentId: null
      });
    }
    return mark;
  }) : [];
  let childrenChanged = false;
  const newChildNodes = [];
  node.content.forEach((child) => {
    const processedChild = updateReplaceOldHighlight(child, state);
    if (processedChild && processedChild !== child) {
      childrenChanged = true;
      newChildNodes.push(processedChild);
    } else {
      newChildNodes.push(child);
    }
  });
  if (!childrenChanged && !newMarks.length) {
    return node;
  }
  if (node.type.isText) {
    return state.schema.text(node.text ?? "", newMarks);
  }
  return node.type.create(node.attrs, Fragment.fromArray(newChildNodes), newMarks);
};
const LegacyHighlight = Mark.create({
  priority: 1,
  name: "highlight",
  parseHTML() {
    return [
      {
        tag: "mark"
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "mark",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  }
});
const LegacyHighlightConverter = Extension.create({
  name: "legacyHighlightConverter",
  addExtensions() {
    return [
      LegacyHighlight
    ];
  },
  onTransaction({ transaction }) {
    const meta = transaction.getMeta(ySyncPluginKey);
    if (!isDefined(meta) || meta.isFirstRender !== true) return;
    const view = this.editor.view;
    const { state } = view;
    if (!view || !state || !this.editor.isEditable) return;
    const tr = convertToFbc(state, updateReplaceOldHighlight);
    if (tr) view.dispatch(tr);
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("highlightConverter"),
        appendTransaction: (transactions, oldState, newState) => {
          const docChanged = transactions.reduce((acc, t) => {
            if (t.getMeta("$convertToFbc")) {
              return acc;
            }
            return t.docChanged ? true : acc;
          }, false);
          if (docChanged) return null;
          const tr = convertToFbc(newState, updateReplaceOldHighlight, oldState.doc);
          if (tr) {
            return tr;
          }
          return null;
        }
      })
    ];
  }
});

export { LegacyHighlightConverter as default };
//# sourceMappingURL=LegacyHighlightConverter.js.map
